import { letterFormText, wordText } from "@talent/tasks";
import type { CardPainter } from "./choice-task.js";
import { fitGlyph, letterGlyph, wordGlyph } from "./glyph.js";

/**
 * The cards for "which word is this letter in": a letter above, words below.
 *
 * One painter for both, because `choice-task.ts` draws the target and the options through the same
 * one — and here they are genuinely different things. The card knows which it is from its `state`:
 * the renderer passes `"target"` for the card at the top and never for an option, so that is the
 * signal, and it is the only place in the codebase where a painter draws two kinds of content.
 *
 * The alternative was to widen the stimulus so each card carried a tag saying what it was. It would
 * be more explicit and it would also put a constant in the event record on every trial forever, to
 * say something the bundle already fixes.
 */

/** The target letter, relative to its card. Large: it is the thing being searched for. */
const LETTER_SIZE = 0.52;

/**
 * A word, relative to its card. Much smaller than a letter, because a word is up to seven of them.
 *
 * It does not have to clear the touch floor and that is the point of this game's design — the whole
 * CARD is the target, so the text only has to be readable, not hittable. Tapping a letter inside a
 * word would have needed 76 px per letter and there is not room for that on a phone.
 */
const WORD_SIZE = 0.3;

/** How much of the card the text's ink may take, in both directions. */
const FIT_ROOM = 0.78;

export const paintWordChoice: CardPainter = (g, content, size, state, ink) => {
  const index = content[0];
  if (index === undefined) return;

  /**
   * The card knows which of the two things it is from its state, and draws accordingly.
   *
   * Fitted and centred on the ink in both directions, like every glyph here. Words run from two
   * letters to seven and the ones carrying marks below the line are much taller than the ones that
   * do not, so no single font size is right for all of them. Only ever shrinks: scaling a short word
   * up would make size mean something, and "tap the biggest text" is a rule a child would find
   * before they found the letter.
   */
  const isTarget = state === "target";
  const fontSize = size * (isTarget ? LETTER_SIZE : WORD_SIZE);
  const glyph = isTarget
    ? // The letter to look for, standing alone — the shape the child is holding in their head.
      letterGlyph(letterFormText(index, 0), { size: fontSize, color: ink, weight: "800" })
    : wordGlyph(wordText(index), { size: fontSize, color: ink, weight: "700" });

  fitGlyph(glyph, glyph.text, fontSize, size * FIT_ROOM);

  g.addChild(glyph);
};
